import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import RecurringScheduleModal from '../../../components/ui/RecurringScheduleModal';
import { getRecurringDescription } from '../../../utils/recurringUtils';

const ActivityModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  activity = null,
  persons,
  selectedDate = null
}) => {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    personIds: [],
    location: '',
    description: '',
    category: 'personal',
    recurringConfig: null
  });

  const [errors, setErrors] = useState({});
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false);

  useEffect(() => {
    if (activity) {
      setFormData({
        title: activity?.title || '',
        date: activity?.date || '',
        time: activity?.time || '',
        personIds: activity?.personIds || (activity?.personId ? [activity?.personId] : []),
        location: activity?.location || '',
        description: activity?.description || '',
        category: activity?.category || 'personal',
        recurringConfig: activity?.recurringConfig || null
      });
    } else if (selectedDate) {
      const dateStr = selectedDate?.toISOString()?.split('T')?.[0];
      setFormData(prev => ({
        ...prev,
        date: dateStr,
        time: '09:00',
        personIds: [],
        recurringConfig: null
      }));
    }
  }, [activity, selectedDate]);

  const categoryOptions = [
    { value: 'personal', label: 'Personal' },
    { value: 'work', label: 'Work' },
    { value: 'school', label: 'School' },
    { value: 'health', label: 'Health' },
    { value: 'social', label: 'Social' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' }
  ];

  const personOptions = persons?.map(person => ({
    value: person?.id?.toString(),
    label: person?.name,
    description: person?.role
  }));

  const togglePersonSelection = (personId) => {
    setFormData(prev => {
      const currentIds = prev?.personIds || [];
      const isSelected = currentIds?.includes(personId);
      
      if (isSelected) {
        return {
          ...prev,
          personIds: currentIds?.filter(id => id !== personId)
        };
      } else {
        return {
          ...prev,
          personIds: [...currentIds, personId]
        };
      }
    });
  };

  const toggleAllPersons = () => {
    setFormData(prev => {
      const allIds = persons?.map(p => p?.id) || [];
      const allSelected = allIds?.length > 0 && allIds?.every(id => prev?.personIds?.includes(id));
      
      return {
        ...prev,
        personIds: allSelected ? [] : allIds
      };
    });
  };

  const getAssignmentVisual = () => {
    const selectedIds = formData?.personIds || [];
    const allIds = persons?.map(p => p?.id) || [];
    
    if (selectedIds?.length === 0) {
      return null;
    }
    
    const allSelected = allIds?.length > 0 && allIds?.every(id => selectedIds?.includes(id));
    
    if (allSelected) {
      // Glittery rainbow effect for all members
      return (
        <div className="relative h-8 rounded-lg overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-indigo-500 to-purple-500 animate-pulse" />
          <div className="absolute inset-0 bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 via-indigo-400 to-purple-400 opacity-70 animate-shimmer" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-semibold text-sm drop-shadow-lg z-10">All Family Members ✨</span>
          </div>
        </div>
      );
    } else {
      // Gradient blend of selected member colors
      const selectedPersons = persons?.filter(p => selectedIds?.includes(p?.id)) || [];
      const colors = selectedPersons?.map(p => p?.color);
      
      if (colors?.length === 1) {
        return (
          <div 
            className="h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: colors?.[0] }}
          >
            <span className="text-white font-semibold text-sm drop-shadow">
              {selectedPersons?.[0]?.name}
            </span>
          </div>
        );
      }
      
      const gradientStops = colors?.map((color, idx) => {
        const position = (idx / (colors?.length - 1)) * 100;
        return `${color} ${position}%`;
      })?.join(', ');
      
      return (
        <div 
          className="h-8 rounded-lg flex items-center justify-center"
          style={{ background: `linear-gradient(90deg, ${gradientStops})` }}
        >
          <span className="text-white font-semibold text-sm drop-shadow">
            {selectedPersons?.length} Members Selected
          </span>
        </div>
      );
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.title?.trim()) {
      newErrors.title = 'Activity title is required';
    }
    
    if (!formData?.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData?.time) {
      newErrors.time = 'Time is required';
    }
    
    if (!formData?.personIds || formData?.personIds?.length === 0) {
      newErrors.personIds = 'Please assign to at least one family member';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const selectedPersons = persons?.filter(p => formData?.personIds?.includes(p?.id));
      
      const activityData = {
        id: activity?.id || Date.now(),
        ...formData,
        personNames: selectedPersons?.map(p => p?.name),
        personColors: selectedPersons?.map(p => p?.color),
        personInitials: selectedPersons?.map(p => p?.initials),
        // Keep backward compatibility
        personId: formData?.personIds?.[0],
        personName: selectedPersons?.[0]?.name,
        personColor: selectedPersons?.[0]?.color,
        createdAt: activity?.createdAt || new Date()?.toISOString()
      };
      
      onSave(activityData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      personIds: [],
      location: '',
      description: '',
      category: 'personal',
      recurringConfig: null
    });
    setErrors({});
    onClose();
  };

  const handleRecurringSave = (recurringData) => {
    setFormData(prev => ({
      ...prev,
      recurringConfig: recurringData
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      >
        <div 
          className="bg-card rounded-xl border border-border shadow-warm-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e?.stopPropagation()}
        >
          <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
            <h2 className="text-xl md:text-2xl font-semibold text-foreground">
              {activity ? 'Edit Activity' : 'Add New Activity'}
            </h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              aria-label="Close modal"
            >
              <Icon name="x" size={24} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
            <Input
              label="Activity Title"
              type="text"
              placeholder="Enter activity name"
              value={formData?.title}
              onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
              error={errors?.title}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Date"
                type="date"
                value={formData?.date}
                onChange={(e) => setFormData({ ...formData, date: e?.target?.value })}
                error={errors?.date}
                required
              />

              <Input
                label="Time"
                type="time"
                value={formData?.time}
                onChange={(e) => setFormData({ ...formData, time: e?.target?.value })}
                error={errors?.time}
                required
              />
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-foreground">
                Assign to Family Members *
              </label>
              
              {getAssignmentVisual()}
              
              <div className="border border-input rounded-lg p-4 bg-background space-y-3">
                <button
                  type="button"
                  onClick={toggleAllPersons}
                  className="w-full flex items-center justify-between p-3 rounded-lg border-2 border-dashed border-input hover:border-primary hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded border-2 border-input flex items-center justify-center">
                      {persons?.length > 0 && persons?.every(p => formData?.personIds?.includes(p?.id)) && (
                        <Icon name="check" size={14} className="text-primary" />
                      )}
                    </div>
                    <span className="font-medium text-foreground">Select All Family Members</span>
                  </div>
                  <span className="text-xs text-muted-foreground">✨ Rainbow effect</span>
                </button>
                
                <div className="space-y-2">
                  {persons?.map(person => {
                    const isSelected = formData?.personIds?.includes(person?.id);
                    
                    return (
                      <button
                        key={person?.id}
                        type="button"
                        onClick={() => togglePersonSelection(person?.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg border border-input hover:border-primary hover:bg-accent transition-colors"
                      >
                        <div className="w-5 h-5 rounded border-2 border-input flex items-center justify-center">
                          {isSelected && (
                            <Icon name="check" size={14} className="text-primary" />
                          )}
                        </div>
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                          style={{ backgroundColor: person?.color }}
                        >
                          {person?.initials}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-foreground">{person?.name}</div>
                          <div className="text-xs text-muted-foreground">{person?.role}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {errors?.personIds && (
                <p className="text-sm text-destructive mt-1">{errors?.personIds}</p>
              )}
            </div>

            <Select
              label="Category"
              options={categoryOptions}
              value={formData?.category}
              onChange={(value) => setFormData({ ...formData, category: value })}
              placeholder="Select category"
            />

            <Input
              label="Location"
              type="text"
              placeholder="Enter location (optional)"
              value={formData?.location}
              onChange={(e) => setFormData({ ...formData, location: e?.target?.value })}
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                className="w-full min-h-[100px] p-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Add notes or details about this activity"
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Recurring Schedule
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsRecurringModalOpen(true)}
                className="w-full justify-between"
              >
                <div className="flex items-center gap-2">
                  <Icon name="repeat" size={16} />
                  <span className="text-sm">
                    {formData?.recurringConfig ? getRecurringDescription(formData?.recurringConfig) : 'Does not repeat'}
                  </span>
                </div>
                <Icon name="chevron-right" size={16} />
              </Button>
            </div>

            <div className="flex flex-col-reverse md:flex-row gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex-1"
              >
                {activity ? 'Update Activity' : 'Add Activity'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <RecurringScheduleModal
        isOpen={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        onSave={handleRecurringSave}
        initialData={formData?.recurringConfig}
      />
    </>
  );
};

export default ActivityModal;